// ** React Imports
import { useEffect, useState } from 'react';

// ** Redux Imports
import { useSelector } from 'react-redux';

// ** MUI Imports
import { useMediaQuery } from '@mui/material';

// ** Chart Imports
import Chart from 'react-apexcharts';

// ** Utils Imports
import { API } from '../../../../../../utility/API';
import { formatNumber } from '../../../../../helpers/formatNumber';

// ** Styles Imports
import styles from './styles.module.css';

// ** Component Imports

// Portfolio launch date - December 10, 2025
const PORTFOLIO_LAUNCH_DATE = new Date('2025-12-10');

const ALL_TIME_PERIODS = [
  { label: '7D', value: '7d', minDays: 7 },
  { label: '30D', value: '30d', minDays: 30 },
  { label: '90D', value: '90d', minDays: 90 },
  { label: '1Y', value: '1y', minDays: 365 },
  { label: 'All', value: 'all', minDays: 1 }
];

function PortfolioGraph({ onMetricsUpdate }) {
  // ** Media Queries
  const isMobileView = useMediaQuery('(max-width:768px)');

  // ** Redux State
  const hideBalance = useSelector(state => state.userData.value.hide_balance);
  const theme = useSelector(state => state.theme.value);

  // ** Calculate days since portfolio launch
  const getDaysSinceLaunch = () => {
    const today = new Date();
    const timeDiff = today.getTime() - PORTFOLIO_LAUNCH_DATE.getTime();
    const days = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include launch day
    console.log('Today:', today.toDateString());
    console.log('Launch Date:', PORTFOLIO_LAUNCH_DATE.toDateString());
    console.log('Days since launch:', days);
    return days;
  };

  // ** Get available time periods based on days since launch
  const getAvailableTimePeriods = () => {
    const daysSinceLaunch = getDaysSinceLaunch();
    console.log('Days since launch:', daysSinceLaunch); // Debug log
    return ALL_TIME_PERIODS.filter(period => {
      const isAvailable = daysSinceLaunch >= period.minDays;
      console.log(`Period ${period.label} (${period.minDays} days): ${isAvailable ? 'Available' : 'Not Available'}`); // Debug log
      return isAvailable;
    });
  };

  // ** Get available periods and default period
  const [availableTimePeriods, setAvailableTimePeriods] = useState([]);
  const getDefaultPeriod = () => {
    const daysSinceLaunch = getDaysSinceLaunch();
    if (daysSinceLaunch >= 30) return '30d';
    return '7d'; // Default to 7d if less than 30 days
  };

  // ** States
  const [selectedPeriod, setSelectedPeriod] = useState('7d'); // Start with 7d, will be updated in useEffect
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState({
    cashBalanceData: [],
    portfolioValueData: [],
    labels: [],
    currentMetrics: {
      cashBalance: 0,
      portfolioValue: 0,
      profitLoss: 0,
      profitLossPercentage: 0
    }
  });


  // ** API Functions
  const fetchBlueLineData = async (duration) => {
    try {
      const response = await API.get(`/trading/account/stats/blue_line?duration=${duration}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching blue line data:', error);
      throw error;
    }
  };

  const fetchGreenLineData = async (duration) => {
    try {
      const response = await API.get(`/trading/account/stats/green_line?duration=${duration}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching green line data:', error);
      throw error;
    }
  };


  // ** Data Processing Functions
  const processGraphData = (blueLineResponse, greenLineResponse) => {
    // Extract stats from API responses
    const blueLineStats = blueLineResponse.stats || {};
    const greenLineStats = greenLineResponse.stats || {};

    // Get all dates and sort them - show ALL dates for day-by-day values
    const allDatesRaw = [...new Set([...Object.keys(blueLineStats), ...Object.keys(greenLineStats)])].sort();

    // Filter dates to only include December 10, 2024 onwards
    const launchDateStr = PORTFOLIO_LAUNCH_DATE.toISOString().split('T')[0]; // '2024-12-10'
    const allDates = allDatesRaw.filter(dateStr => dateStr >= launchDateStr);

    // Use all dates without filtering for day-by-day display
    const labels = allDates.map(dateStr => {
      const date = new Date(dateStr);
      // Adjust label format based on number of data points and period
      if (selectedPeriod === '7d' || allDates.length <= 7) {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      } else if (selectedPeriod === '30d' || allDates.length <= 30) {
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      } else if (selectedPeriod === '90d' || allDates.length <= 90) {
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: '2-digit'
        });
      }
    });

    const cashBalanceData = allDates.map(dateStr => {
      const dayData = blueLineStats[dateStr];
      return Math.max(0, parseFloat(dayData?.cash || 0)); // Ensure never negative
    });

    const portfolioValueData = allDates.map(dateStr => {
      const dayData = greenLineStats[dateStr];
      return Math.max(0, parseFloat(dayData?.portfolio_amount || 0));
    });

    // Calculate current metrics from the latest data
    const latestDate = allDates[allDates.length - 1];
    const latestBlueData = blueLineStats[latestDate] || {};
    const latestGreenData = greenLineStats[latestDate] || {};

    const currentCashBalance = parseFloat(latestBlueData.cash || 0);
    const currentPortfolioValue = parseFloat(latestGreenData.portfolio_amount || 0);
    const profitLoss = parseFloat(latestGreenData.pl_amount || 0);
    const profitLossPercentage = parseFloat(latestGreenData.pl_percentage || 0);

    return {
      cashBalanceData,
      portfolioValueData,
      labels,
      currentMetrics: {
        cashBalance: currentCashBalance,
        portfolioValue: currentPortfolioValue,
        profitLoss,
        profitLossPercentage
      }
    };
  };

  // ** Demo Data Generator (for testing when API is not available)
  const generateDemoData = (period) => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : period === '1y' ? 365 : 30;
    const today = new Date();
    const daysSinceLaunch = getDaysSinceLaunch();
    const actualDays = Math.min(days, daysSinceLaunch);

    // Start from launch date, not from calculated days ago
    const startDate = new Date(PORTFOLIO_LAUNCH_DATE);

    let cashBalance = 10000; // Starting cash balance
    let portfolioValue = 10000; // Starting portfolio value

    const blueLineStats = {};
    const greenLineStats = {};

    for (let i = 0; i < actualDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format

      // Simulate some market volatility for portfolio value
      const volatility = (Math.random() - 0.5) * 0.05; // ±2.5% daily change
      portfolioValue = Math.max(0, portfolioValue * (1 + volatility));

      // Occasionally add deposits (cash balance increases)
      if (Math.random() < 0.1) { // 10% chance of deposit
        const deposit = Math.random() * 2000 + 500; // $500-$2500 deposit
        cashBalance += deposit;
        portfolioValue += deposit;
      }

      // Blue line data (cash balance)
      blueLineStats[dateStr] = {
        deposits: 0,
        withdrawals: 0,
        cash: cashBalance.toString()
      };

      // Green line data (portfolio value)
      const profitLoss = portfolioValue - cashBalance;
      const profitLossPercentage = cashBalance > 0 ? (profitLoss / cashBalance) * 100 : 0;

      greenLineStats[dateStr] = {
        cash_amount: cashBalance,
        portfolio_amount: portfolioValue,
        pl_amount: profitLoss,
        pl_percentage: profitLossPercentage
      };
    }

    return {
      blueLineResponse: { stats: blueLineStats },
      greenLineResponse: { stats: greenLineStats }
    };
  };

  // ** Load Graph Data
  const loadGraphData = async (period = selectedPeriod) => {
    setLoading(true);
    try {
      const [blueLineResponse, greenLineResponse] = await Promise.all([
        fetchBlueLineData(period),
        fetchGreenLineData(period)
      ]);

      // Process the API responses directly
      const processedData = processGraphData(blueLineResponse, greenLineResponse);
      setGraphData(processedData);
    } catch (error) {
      console.error('Error loading graph data:', error);
      // Fallback to demo data if API fails
      console.log('API error, using demo data');
      const { blueLineResponse, greenLineResponse } = generateDemoData(period);
      const processedData = processGraphData(blueLineResponse, greenLineResponse);
      setGraphData(processedData);
    } finally {
      setLoading(false);
    }
  };

  // ** Effects
  useEffect(() => {
    // Initialize available periods and default selection
    const periods = getAvailableTimePeriods();
    setAvailableTimePeriods(periods);

    const defaultPeriod = getDefaultPeriod();
    const isDefaultAvailable = periods.some(period => period.value === defaultPeriod);

    if (isDefaultAvailable) {
      setSelectedPeriod(defaultPeriod);
    } else if (periods.length > 0) {
      setSelectedPeriod(periods[0].value); // Use first available period
    }
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      loadGraphData(selectedPeriod);
    }
  }, [selectedPeriod]);

  // ** Update parent component with current metrics
  useEffect(() => {
    if (onMetricsUpdate && graphData.currentMetrics) {
      onMetricsUpdate(graphData.currentMetrics);
    }
  }, [graphData.currentMetrics, onMetricsUpdate]);

  // ** Handlers
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // ** Chart Configuration
  const chartHeight = isMobileView ? 250 : 300;

  const chartOptions = {
    chart: {
      type: 'line',
      height: chartHeight,
      background: 'transparent',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    theme: {
      mode: theme === 'light' ? 'light' : 'dark'
    },
    colors: ['#3b82f6', '#10b981'],
    stroke: {
      width: [2, 2],
      curve: ['straight', 'straight'], // Both lines straight for sharp day-to-day transitions
      dashArray: [5, 0] // First line dashed, second solid
    },
    grid: {
      borderColor: theme === 'light' ? '#f0f0f0' : '#333',
      strokeDashArray: 0,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    xaxis: {
      categories: graphData.labels,
      labels: {
        style: {
          colors: theme === 'light' ? '#666' : '#999',
          fontSize: '11px'
        },
        rotate: isMobileView ? -45 : (graphData.labels.length > 15 ? -45 : 0), // Rotate labels when many data points
        rotateAlways: false,
        hideOverlappingLabels: true,
        showDuplicates: false,
        trim: true,
        maxHeight: 60 // Limit height for rotated labels
      },
      // Control number of ticks based on data length and period
      tickAmount: (() => {
        const totalLabels = graphData.labels.length;
        if (selectedPeriod === '7d') return Math.min(totalLabels - 1, 6);
        if (selectedPeriod === '30d') return Math.min(totalLabels - 1, 8);
        if (selectedPeriod === '90d') return Math.min(totalLabels - 1, 10);
        if (selectedPeriod === '1y') return Math.min(totalLabels - 1, 12);
        if (selectedPeriod === 'all') return Math.min(totalLabels - 1, 15);
        return Math.min(totalLabels - 1, 10);
      })(),
      axisBorder: {
        color: theme === 'light' ? '#ddd' : '#555'
      },
      axisTicks: {
        color: theme === 'light' ? '#ddd' : '#555'
      },
      tooltip: {
        enabled: false // Hide the x-axis tooltip
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: theme === 'light' ? '#666' : '#999',
          fontSize: '12px'
        },
        formatter: (value) => `$${value.toLocaleString()}`
      }
    },
    tooltip: {
      theme: theme === 'light' ? 'light' : 'dark',
      shared: true,
      intersect: false,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const formattedDate = w.globals.categoryLabels[dataPointIndex] || 'N/A';

        const cashValue = series[0][dataPointIndex] || 0;
        const portfolioValue = series[1][dataPointIndex] || 0;
        const profitLoss = portfolioValue - cashValue;

        const formatCurrency = (value) => {
          return `$${formatNumber(value, 2)}`;
        };

        return `
          <div style="
            background: var(--secondary-color); 
            border: 1px solid rgba(139, 154, 171, 0.1); 
            border-radius: 8px; 
            padding: 12px 16px; 
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            min-width: 160px;
          ">
            <div style="
              color: var(--gray-color); 
              font-size: 14px; 
              font-weight: 500; 
              margin-bottom: 8px;
            ">
              ${formattedDate}
            </div>
            
            <div style="margin-bottom: 6px;">
              <span style="
                color: #10b981; 
                font-size: 14px; 
                font-weight: 500;
              ">Portfolio:</span>
              <span style="
                color: var(--text-color); 
                font-size: 14px; 
                font-weight: 600; 
                margin-left: 8px;
              ">
                ${formatCurrency(portfolioValue)}
              </span>
            </div>
            
            <div style="margin-bottom: 6px;">
              <span style="
                color: #3b82f6; 
                font-size: 14px; 
                font-weight: 500;
              ">Cash:</span>
              <span style="
                color: var(--text-color); 
                font-size: 14px; 
                font-weight: 600; 
                margin-left: 8px;
              ">
                ${formatCurrency(cashValue)}
              </span>
            </div>
            
            <div>
              <span style="
                color: var(--gray-color); 
                font-size: 14px; 
                font-weight: 500;
              ">P/L:</span>
              <span style="
                color: ${profitLoss >= 0 ? '#10b981' : '#ef4444'}; 
                font-size: 14px; 
                font-weight: 600; 
                margin-left: 8px;
              ">
                ${profitLoss >= 0 ? '+' : ''}${formatCurrency(profitLoss)}
              </span>
            </div>
          </div>
        `;
      }
    },
    legend: {
      show: false // We'll use custom legend
    },
    markers: {
      size: 0,
      hover: {
        size: 6
      }
    }
  };

  const chartSeries = [
    {
      name: 'Cash Balance',
      data: graphData.cashBalanceData
    },
    {
      name: 'Portfolio Value',
      data: graphData.portfolioValueData
    }
  ];

  return (
    <div className={styles.portfolioGraphContainer}>
      {/* Header Section */}
      <div className={styles.graphHeader}>
        <div className={styles.titleSection}>
          <h3 className={styles.graphTitle}>Portfolio Performance</h3>
          <p className={styles.graphSubtitle}>
            Track your investment journey over time
          </p>
        </div>

        {/* Time Period Selector */}
        <div className={styles.periodSelector}>
          {availableTimePeriods.map((period) => (
            <button
              key={period.value}
              className={`${styles.periodButton} ${selectedPeriod === period.value ? styles.active : ''
                }`}
              onClick={() => handlePeriodChange(period.value)}
              disabled={loading}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Metrics Cards */}
      {/* <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Cash Balance</div>
          <div className={styles.metricValue}>
            {loading ? (
              <div className={styles.shimmerEffect} style={{ width: '100px', height: '24px' }}></div>
            ) : hideBalance ? (
              '••••••••'
            ) : (
              <AmountDisplay amount={graphData.currentMetrics.cashBalance} coinId="usd" />
            )}
          </div>
          <div className={styles.metricDescription}>Net deposits - withdrawals</div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Portfolio Value</div>
          <div className={styles.metricValue}>
            {loading ? (
              <div className={styles.shimmerEffect} style={{ width: '100px', height: '24px' }}></div>
            ) : hideBalance ? (
              '••••••••'
            ) : (
              <AmountDisplay amount={graphData.currentMetrics.portfolioValue} coinId="usd" />
            )}
          </div>
          <div className={styles.metricDescription}>Current total asset value</div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricLabel}>Profit/Loss</div>
          <div className={`${styles.metricValue} ${graphData.currentMetrics.profitLoss >= 0 ? styles.positive : styles.negative
            }`}>
            {loading ? (
              <div className={styles.shimmerEffect} style={{ width: '100px', height: '24px' }}></div>
            ) : hideBalance ? (
              '••••••••'
            ) : (
              <>
                {graphData.currentMetrics.profitLoss >= 0 ? '+' : ''}
                <AmountDisplay amount={Math.abs(graphData.currentMetrics.profitLoss)} coinId="usd" />
                <span className={styles.percentage}>
                  ({graphData.currentMetrics.profitLossPercentage >= 0 ? '+' : ''}
                  {graphData.currentMetrics.profitLossPercentage.toFixed(2)}%)
                </span>
              </>
            )}
          </div>
          <div className={styles.metricDescription}>
            {graphData.currentMetrics.profitLoss >= 0 ? 'Total gains' : 'Total losses'}
          </div>
        </div>
      </div> */}

      {/* Graph Section */}
      <div className={styles.graphSection}>
        {loading ? (
          <div className={styles.graphLoading}>
            <div className={styles.shimmerEffect} style={{ width: '100%', height: `${chartHeight}px` }}></div>
          </div>
        ) : (
          <div style={{ width: '100%', height: chartHeight }}>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={chartHeight}
            />
          </div>
        )}

      </div>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendLine} ${styles.cashBalanceLine}`}></div>
          <span>Cash Balance (Deposits - Withdrawals)</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendLine} ${styles.portfolioValueLine}`}></div>
          <span>Portfolio Value (Current Asset Worth)</span>
        </div>
      </div>

      {/* Disclaimer */}
      <div className={styles.disclaimer}>
        <p className={styles.disclaimerText}>
          <strong>Disclaimer:</strong> Kindly note that the Portfolio Performance Graph has been operational since December 10, 2025. As a result, any transactions executed prior to this date are not reflected in the graph.
        </p>
      </div>
    </div>
  );
}

export default PortfolioGraph;
